import type { InjectionToken } from "tsyringe";
import { inject, injectable } from "tsyringe";
import type {
  BoxListItem,
  BoxesPageInput,
  IWarehouseApi,
  PagedResult,
} from "../WarehouseApi";
import { WAREHOUSE_API_TOKEN } from "../WarehouseApi";
import type {
  BoxStorageRecord,
  DataStorage,
  ProductStorageRecord,
} from "../../domain/storage";
import type { IProduct } from "../../types/entities";
import { createBoxId, createProductId } from "../../types/ids";
import {
  DATA_STORAGE_FACTORY_TOKEN,
  type IDataStorageFactory,
} from "./DataStorageFactory";
import { resolveRuntimeStorageProvider } from "./StorageProviderFeatureFlag";

export interface IStorageReadFacade {
  getBoxesPage(input?: BoxesPageInput): Promise<PagedResult<BoxListItem>>;
  getBoxById(id: string): Promise<BoxListItem | null>;
  getProductById(id: string): Promise<IProduct | null>;
}

export const STORAGE_READ_FACADE_TOKEN = Symbol(
  "IStorageReadFacade",
) as InjectionToken<IStorageReadFacade>;

function nowIsoString(): string {
  return new Date().toISOString();
}

function toBoxStorageRecord(item: BoxListItem): BoxStorageRecord {
  return {
    id: createBoxId(item.id),
    name: item.name,
    photo: item.photo,
    description: item.description,
    typeTags: item.typeTags,
    parentId: item.parentId ? createBoxId(item.parentId) : null,
    updatedAt: nowIsoString(),
    version: 1,
    syncState: "synced",
  };
}

function toBoxListItem(record: BoxStorageRecord): BoxListItem {
  return {
    id: record.id,
    name: record.name,
    photo: record.photo,
    description: record.description,
    typeTags: record.typeTags,
    parentId: record.parentId,
  };
}

function toProductStorageRecord(item: IProduct): ProductStorageRecord {
  return {
    ...item,
    updatedAt: nowIsoString(),
    version: 1,
    syncState: "synced",
  };
}

function toProduct(record: ProductStorageRecord): IProduct {
  return {
    id: record.id,
    name: record.name,
    photo: record.photo,
    description: record.description,
    manufacturingDate: record.manufacturingDate,
    expiryDate: record.expiryDate,
    typeTags: record.typeTags,
    measure: record.measure,
    quantity: record.quantity,
    parentId: record.parentId,
  };
}

@injectable()
export class StorageReadFacade implements IStorageReadFacade {
  private storagePromise: Promise<DataStorage> | null = null;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(DATA_STORAGE_FACTORY_TOKEN)
    private readonly storageFactory: IDataStorageFactory,
  ) {}

  async getBoxesPage(
    input?: BoxesPageInput,
  ): Promise<PagedResult<BoxListItem>> {
    const page = await this.api.getBoxesPage(input);
    const storage = await this.getStorage();

    await Promise.all(
      page.items.map(async (item) => {
        await storage.boxes.upsert(toBoxStorageRecord(item));
      }),
    );

    return page;
  }

  async getBoxById(id: string): Promise<BoxListItem | null> {
    const storage = await this.getStorage();
    const cached = await storage.boxes.getById(createBoxId(id));
    if (cached) {
      return toBoxListItem(cached);
    }

    const remote = await this.api.getBoxById(id);
    if (!remote) {
      return null;
    }

    await storage.boxes.upsert(toBoxStorageRecord(remote));
    return remote;
  }

  async getProductById(id: string): Promise<IProduct | null> {
    const storage = await this.getStorage();
    const cached = await storage.products.getById(createProductId(id));
    if (cached) {
      return toProduct(cached);
    }

    const remote = await this.api.getProductById(id);
    if (!remote) {
      return null;
    }

    await storage.products.upsert(toProductStorageRecord(remote));
    return remote;
  }

  private async getStorage(): Promise<DataStorage> {
    if (!this.storagePromise) {
      this.storagePromise = this.createStorageWithFallback();
    }
    return this.storagePromise;
  }

  private async createStorageWithFallback(): Promise<DataStorage> {
    const provider = resolveRuntimeStorageProvider();

    try {
      return await this.storageFactory.create(provider);
    } catch (error) {
      if (provider !== "local") {
        return this.storageFactory.create("local");
      }
      throw error;
    }
  }
}
